/**
 * Theme Context and Provider
 * 
 * React context for managing theme state and switching between themes
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Theme, ThemeName } from '../styles/themes';
import { getTheme, generateCSSVariables } from '../styles/themes';

// =============================================================================
// THEME TYPES
// =============================================================================

export type ThemePreference = 'light' | 'dark' | 'auto';

// =============================================================================
// THEME UTILITIES
// =============================================================================

/**
 * Resolve theme preference to actual theme name
 */
const resolveThemePreference = (preference: ThemePreference): ThemeName => {
  if (preference === 'auto') {
    // Check system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light'; // fallback
  }
  return preference as ThemeName;
};

/**
 * Get available theme preferences
 */
const getAvailableThemePreferences = (): { name: ThemePreference; displayName: string; isDark?: boolean }[] => {
  return [
    { name: 'auto', displayName: 'Auto (System)', isDark: undefined },
    { name: 'light', displayName: 'Light', isDark: false },
    { name: 'dark', displayName: 'Dark', isDark: true },
  ];
};

// =============================================================================
// THEME CONTEXT
// =============================================================================

interface ThemeContextType {
  currentTheme: Theme;
  themeName: ThemeName;
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  isDark: boolean;
  availableThemes: { name: ThemePreference; displayName: string; isDark?: boolean }[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// =============================================================================
// THEME PROVIDER
// =============================================================================

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemePreference;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  defaultTheme = 'auto' 
}) => {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => {
    // Try to load saved theme preference from localStorage
    const savedPreference = localStorage.getItem('performanceCockpit-theme-preference');
    return (savedPreference as ThemePreference) || defaultTheme;
  });

  const [themeName, setThemeName] = useState<ThemeName>(() => {
    return resolveThemePreference(themePreference);
  });

  const currentTheme = getTheme(themeName);

  const setThemePreference = (newPreference: ThemePreference) => {
    setThemePreferenceState(newPreference);
    // Save to localStorage
    localStorage.setItem('performanceCockpit-theme-preference', newPreference);
    // Resolve the actual theme
    const resolvedTheme = resolveThemePreference(newPreference);
    setThemeName(resolvedTheme);
  };

  // Listen for system theme changes when using auto
  useEffect(() => {
    if (themePreference === 'auto' && typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        const newTheme = e.matches ? 'dark' : 'light';
        console.log('🎨 System theme changed to:', newTheme);
        setThemeName(newTheme);
      };

      mediaQuery.addEventListener('change', handleChange);
      
      // Set initial theme based on system preference
      const initialTheme = mediaQuery.matches ? 'dark' : 'light';
      console.log('🎨 Auto theme detected system preference:', initialTheme);
      setThemeName(initialTheme);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    } else {
      // For non-auto themes, resolve immediately
      const resolvedTheme = resolveThemePreference(themePreference);
      console.log('🎨 Theme preference set to:', themePreference, '→ resolved theme:', resolvedTheme);
      setThemeName(resolvedTheme);
    }
  }, [themePreference]);

  // Apply CSS variables when theme changes
  useEffect(() => {
    const root = document.documentElement;
    const variables = generateCSSVariables(currentTheme);
    
    // Apply all CSS variables to :root
    Object.entries(variables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Add theme class to body for additional styling
    document.body.className = document.body.className
      .replace(/theme-\w+/g, '') // Remove existing theme classes
      .concat(` theme-${themeName}`)
      .trim();

    // Add dark/light class for compatibility
    if (currentTheme.isDark) {
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    }
  }, [currentTheme, themeName]);

  const availableThemes = getAvailableThemePreferences();

  const contextValue: ThemeContextType = {
    currentTheme,
    themeName,
    themePreference,
    setThemePreference,
    isDark: currentTheme.isDark,
    availableThemes,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// =============================================================================
// THEME HOOK
// =============================================================================

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// =============================================================================
// THEME UTILITIES
// =============================================================================

/**
 * Get the current theme colors directly
 */
export const useThemeColors = () => {
  const { currentTheme } = useTheme();
  return currentTheme.colors;
};

/**
 * Get theme-aware class names
 */
export const useThemeClasses = () => {
  const { isDark } = useTheme();
  
  return {
    // Background utilities
    bgPrimary: isDark ? 'bg-slate-800' : 'bg-white',
    bgSecondary: isDark ? 'bg-slate-700' : 'bg-gray-50',
    bgTertiary: isDark ? 'bg-slate-600' : 'bg-gray-100',
    bgSurface: isDark ? 'bg-slate-800' : 'bg-white',
    bgElevated: isDark ? 'bg-slate-700' : 'bg-white',
    
    // Text utilities
    textPrimary: isDark ? 'text-slate-50' : 'text-gray-900',
    textSecondary: isDark ? 'text-slate-200' : 'text-gray-700',
    textTertiary: isDark ? 'text-slate-300' : 'text-gray-600',
    textMuted: isDark ? 'text-slate-400' : 'text-gray-500',
    textWhite: 'text-white',
    textInverse: isDark ? 'text-slate-900' : 'text-white',
    
    // Status colors
    textSuccess: isDark ? 'text-emerald-400' : 'text-green-500',
    textWarning: isDark ? 'text-yellow-400' : 'text-yellow-500',
    textError: isDark ? 'text-red-400' : 'text-red-500',
    textInfo: isDark ? 'text-blue-400' : 'text-blue-500',
    
    bgSuccess: isDark ? 'bg-emerald-400' : 'bg-green-500',
    bgWarning: isDark ? 'bg-yellow-400' : 'bg-yellow-500',
    bgError: isDark ? 'bg-red-400' : 'bg-red-500',
    bgInfo: isDark ? 'bg-blue-400' : 'bg-blue-500',
    
    // Status background variants (subtle)
    bgSuccessSubtle: isDark ? 'bg-emerald-900/20' : 'bg-green-50',
    bgWarningSubtle: isDark ? 'bg-yellow-900/20' : 'bg-yellow-50',
    bgErrorSubtle: isDark ? 'bg-red-900/20' : 'bg-red-50',
    bgInfoSubtle: isDark ? 'bg-blue-900/20' : 'bg-blue-50',
    
    // Additional semantic colors for UI elements
    bgPurple: isDark ? 'bg-purple-400' : 'bg-purple-500',
    bgOrange: isDark ? 'bg-orange-400' : 'bg-orange-500',
    bgIndigo: isDark ? 'bg-indigo-400' : 'bg-indigo-500',
    bgTeal: isDark ? 'bg-teal-400' : 'bg-teal-500',
    bgPink: isDark ? 'bg-pink-400' : 'bg-pink-500',
    bgGray: isDark ? 'bg-slate-500' : 'bg-gray-500',
    
    textPurple: isDark ? 'text-purple-400' : 'text-purple-600',
    textOrange: isDark ? 'text-orange-400' : 'text-orange-600',
    textIndigo: isDark ? 'text-indigo-400' : 'text-indigo-600',
    textTeal: isDark ? 'text-teal-400' : 'text-teal-600',
    textPink: isDark ? 'text-pink-400' : 'text-pink-600',
    textGray: isDark ? 'text-slate-400' : 'text-gray-600',
    
    // Link colors
    linkPrimary: isDark ? 'text-blue-400' : 'text-blue-600',
    linkHover: isDark ? 'hover:text-blue-300' : 'hover:text-blue-800',
    
    // Subtle background variants for different colors
    bgPurpleSubtle: isDark ? 'bg-purple-900/20' : 'bg-purple-50',
    bgOrangeSubtle: isDark ? 'bg-orange-900/20' : 'bg-orange-50',
    bgIndigoSubtle: isDark ? 'bg-indigo-900/20' : 'bg-indigo-50',
    bgTealSubtle: isDark ? 'bg-teal-900/20' : 'bg-teal-50',
    bgPinkSubtle: isDark ? 'bg-pink-900/20' : 'bg-pink-50',
    bgGraySubtle: isDark ? 'bg-slate-800/30' : 'bg-gray-50',
    
    // Section-specific color schemes (for HelpView sections)
    sectionBlue: isDark ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200',
    sectionGreen: isDark ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-green-50 border-green-200',
    sectionYellow: isDark ? 'bg-yellow-900/20 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200',
    sectionRed: isDark ? 'bg-red-900/20 border-red-500/30' : 'bg-red-50 border-red-200',
    sectionPurple: isDark ? 'bg-purple-900/20 border-purple-500/30' : 'bg-purple-50 border-purple-200',
    sectionOrange: isDark ? 'bg-orange-900/20 border-orange-500/30' : 'bg-orange-50 border-orange-200',
    
    // Border utilities
    borderPrimary: isDark ? 'border-slate-600' : 'border-gray-200',
    borderSecondary: isDark ? 'border-slate-500' : 'border-gray-300',
    borderAccent: isDark ? 'border-blue-500' : 'border-blue-200',
    borderTree: isDark ? 'border-slate-400' : 'border-gray-300', // Specific for tree hierarchy lines
    
    // Brand colors
    bgBrand: 'bg-[#025a94]', // Always use brand color
    textBrand: 'text-[#025a94]',
    bgBrandLight: isDark ? 'bg-blue-400' : 'bg-[#84d1f1]',
    textBrandLight: isDark ? 'text-blue-400' : 'text-[#037ac7]',
    
    // Interactive utilities
    hover: isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100',
    hoverSecondary: isDark ? 'hover:bg-slate-600' : 'hover:bg-gray-200',
    active: isDark ? 'bg-slate-600' : 'bg-gray-200',
    focus: isDark ? 'focus:ring-blue-400' : 'focus:ring-blue-500',
    
    // Card and surface utilities
    cardBg: isDark ? 'bg-slate-800' : 'bg-white',
    cardBorder: isDark ? 'border-slate-600' : 'border-gray-200',
    cardShadow: isDark ? 'shadow-lg shadow-black/20' : 'shadow-sm',
    
    // Input utilities
    inputBg: isDark ? 'bg-slate-700' : 'bg-white',
    inputBorder: isDark ? 'border-slate-600' : 'border-gray-300',
    inputFocus: isDark ? 'focus:border-blue-400 focus:ring-blue-400' : 'focus:border-blue-500 focus:ring-blue-500',
  };
};

/**
 * Get inline styles with theme colors (for components that need direct color values)
 */
export const useThemeStyles = () => {
  const { currentTheme } = useTheme();
  const colors = currentTheme.colors;
  
  return {
    // Background styles
    bgPrimary: { backgroundColor: colors.background.primary },
    bgSecondary: { backgroundColor: colors.background.secondary },
    bgSurface: { backgroundColor: colors.background.surface },
    
    // Text styles
    textPrimary: { color: colors.text.primary },
    textSecondary: { color: colors.text.secondary },
    textMuted: { color: colors.text.muted },
    
    // Status styles
    textSuccess: { color: colors.status.success },
    textWarning: { color: colors.status.warning },
    textError: { color: colors.status.error },
    textInfo: { color: colors.status.info },
    
    bgSuccess: { backgroundColor: colors.status.success },
    bgWarning: { backgroundColor: colors.status.warning },
    bgError: { backgroundColor: colors.status.error },
    bgInfo: { backgroundColor: colors.status.info },
    
    // Border styles
    borderPrimary: { borderColor: colors.border.primary },
    borderSecondary: { borderColor: colors.border.secondary },
  };
};

/**
 * Get semantic color values directly (for chart libraries, etc.)
 */
export const useThemeColorValues = () => {
  const { currentTheme } = useTheme();
  return {
    // Status colors
    success: currentTheme.colors.status.success,
    warning: currentTheme.colors.status.warning,
    error: currentTheme.colors.status.error,
    info: currentTheme.colors.status.info,
    disabled: currentTheme.colors.status.disabled,
    
    // Brand colors
    brand: currentTheme.colors.title,
    brandLight: currentTheme.colors.cockpitBlue.light,
    brandDark: currentTheme.colors.cockpitBlue.dark,
    
    // Background colors
    bgPrimary: currentTheme.colors.background.primary,
    bgSecondary: currentTheme.colors.background.secondary,
    bgSurface: currentTheme.colors.background.surface,
    
    // Text colors
    textPrimary: currentTheme.colors.text.primary,
    textSecondary: currentTheme.colors.text.secondary,
    textMuted: currentTheme.colors.text.muted,
    
    // Border colors
    borderPrimary: currentTheme.colors.border.primary,
    borderSecondary: currentTheme.colors.border.secondary,
  };
};

/**
 * Theme-aware chart colors
 */
export const useChartColors = () => {
  const { currentTheme } = useTheme();
  return currentTheme.chart;
};

/**
 * Get theme-aware component styles
 */
export const useThemeComponentStyles = () => {
  const { currentTheme } = useTheme();
  
  return {
    // Status indicators
    status: {
      base: 'w-3 h-3 rounded-full flex-shrink-0',
      success: `w-3 h-3 rounded-full flex-shrink-0 ${currentTheme.isDark ? 'bg-emerald-400' : 'bg-green-500'}`,
      error: `w-3 h-3 rounded-full flex-shrink-0 ${currentTheme.isDark ? 'bg-red-400' : 'bg-red-500'}`,
      warning: `w-3 h-3 rounded-full flex-shrink-0 ${currentTheme.isDark ? 'bg-yellow-400' : 'bg-yellow-500'}`,
      disabled: `w-3 h-3 rounded-full flex-shrink-0 ${currentTheme.isDark ? 'bg-slate-500' : 'bg-gray-400'}`,
      info: `w-3 h-3 rounded-full flex-shrink-0 ${currentTheme.isDark ? 'bg-blue-400' : 'bg-blue-500'}`,
      
      connection: {
        connected: `w-3 h-3 rounded-full ${currentTheme.isDark ? 'bg-emerald-400' : 'bg-green-400'}`,
        disconnected: `w-3 h-3 rounded-full ${currentTheme.isDark ? 'bg-red-400' : 'bg-red-400'}`,
      },
    },
    
    // Buttons
    button: {
      primary: `px-4 py-2 rounded-md font-medium transition-all duration-200 ${currentTheme.isDark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-700'} text-white`,
      secondary: `px-4 py-2 rounded-md font-medium transition-all duration-200 border ${currentTheme.isDark ? 'border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`,
      success: `px-4 py-2 rounded-md font-medium transition-all duration-200 text-white ${currentTheme.isDark ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-green-600 hover:bg-green-700'}`,
      warning: `px-4 py-2 rounded-md font-medium transition-all duration-200 text-white ${currentTheme.isDark ? 'bg-yellow-600 hover:bg-yellow-500' : 'bg-yellow-600 hover:bg-yellow-700'}`,
      error: `px-4 py-2 rounded-md font-medium transition-all duration-200 text-white ${currentTheme.isDark ? 'bg-red-600 hover:bg-red-500' : 'bg-red-600 hover:bg-red-700'}`,
    },
    
    // Tabs
    tab: {
      selected: `px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow ${currentTheme.isDark ? 'bg-blue-600 text-white border-blue-700' : 'bg-blue-600 text-white border-blue-700'}`,
      unselected: `px-4 py-2 rounded-lg font-medium transition-all duration-200 ${currentTheme.isDark ? 'bg-slate-800 text-blue-400 border-slate-600 hover:bg-slate-700' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'}`,
    },
    
    // Loading indicators
    loading: {
      dot: `w-2 h-2 rounded-full animate-pulse ${currentTheme.isDark ? 'bg-blue-400' : 'bg-blue-600'}`,
    },
    
    // Cards
    card: {
      base: `rounded-lg border transition-all duration-200 ${currentTheme.isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`,
      elevated: `rounded-lg border transition-all duration-200 shadow-sm ${currentTheme.isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'}`,
      interactive: `rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-md ${currentTheme.isDark ? 'bg-slate-800 border-slate-600 hover:bg-slate-700' : 'bg-white border-gray-200 hover:bg-gray-50'}`,
    },
  };
};

export default ThemeProvider;
